/**
 * Example data that satisfies the schemas.
 *
 * Exists so that interface work can proceed against the agreed shapes before
 * any endpoint is implemented — which is what makes the parent and kids
 * applications buildable in parallel with the backend rather than after it.
 *
 * Also used in tests, so there is one definition of "a plausible family" rather
 * than a slightly different one invented in every spec file.
 *
 * Values are fixed, never generated: a fixture that changes between runs makes
 * snapshot differences meaningless.
 */
const id = (value) => value;
export const fixtureFamily = {
    id: id('a1000000-0000-4000-8000-000000000001'),
    name: 'Eren',
    timeZone: 'Europe/Istanbul',
    createdAt: '2026-08-01T09:00:00.000Z',
};
export const fixtureCollectionIds = {
    morning: id('c1000000-0000-4000-8000-000000000001'),
    cartoons: id('c1000000-0000-4000-8000-000000000002'),
    songs: id('c1000000-0000-4000-8000-000000000003'),
    evening: id('c1000000-0000-4000-8000-000000000004'),
};
const contentId = (suffix) => id(`d1000000-0000-4000-8000-0000000000${suffix}`);
/** A three-year-old on a weekday schedule, the product's founding user. */
export const fixtureChild = {
    child: {
        id: id('b1000000-0000-4000-8000-000000000001'),
        familyId: fixtureFamily.id,
        name: 'Ela',
        ageBand: 'TODDLER',
        avatarKey: 'fox',
        createdAt: '2026-08-01T09:05:00.000Z',
    },
    profile: {
        childId: id('b1000000-0000-4000-8000-000000000001'),
        locale: 'tr',
        nativeLanguage: 'tr',
        foreignLanguages: ['en'],
        budget: {
            dailyTotalSeconds: 3600,
            maxSessionSeconds: 1200,
            restPeriodSeconds: 1200,
            perContentTypeSeconds: { VIDEO: 2400, GAME: 1200 },
        },
        schedule: {
            windowsByDay: {
                MON: [
                    { start: '09:00', end: '12:00' },
                    { start: '16:00', end: '19:00' },
                ],
                TUE: [
                    { start: '09:00', end: '12:00' },
                    { start: '16:00', end: '19:00' },
                ],
                WED: [
                    { start: '09:00', end: '12:00' },
                    { start: '16:00', end: '19:00' },
                ],
                THU: [
                    { start: '09:00', end: '12:00' },
                    { start: '16:00', end: '19:00' },
                ],
                FRI: [
                    { start: '09:00', end: '12:00' },
                    { start: '16:00', end: '19:00' },
                ],
                SAT: [{ start: '10:00', end: '18:00' }],
                SUN: [{ start: '10:00', end: '18:00' }],
            },
        },
        enabledCollectionIds: [
            fixtureCollectionIds.morning,
            fixtureCollectionIds.cartoons,
            fixtureCollectionIds.songs,
        ],
        movementBreaksEnabled: true,
        movementBreak: { everySeconds: 900, lastsSeconds: 30, maxPerHour: 2 },
        activeViewingEnabled: false,
        activeViewing: {
            passiveMotionScore: 0.12,
            passiveAfterSeconds: 300,
            interactionCountsForSeconds: 120,
        },
        distanceWarningEnabled: false,
        distanceWarning: { tooCloseCentimetres: 25 },
    },
};
export const fixtureDevice = {
    id: id('e1000000-0000-4000-8000-000000000001'),
    familyId: fixtureFamily.id,
    name: "Ela'nın Tableti",
    platform: 'ANDROID',
    assignedChildIds: [fixtureChild.child.id],
    activeChildId: fixtureChild.child.id,
    isOnline: true,
    lastSeenAt: '2026-08-11T13:40:00.000Z',
    createdAt: '2026-08-01T09:10:00.000Z',
};
/** A snapshot with six tiles, matching the home screen on the design board. */
export const fixtureConfigRevision = {
    revision: 142,
    updatedAt: '2026-08-11T13:00:00.000Z',
    deviceId: fixtureDevice.id,
    child: {
        id: fixtureChild.child.id,
        name: fixtureChild.child.name,
        avatarKey: fixtureChild.child.avatarKey,
    },
    timeZone: fixtureFamily.timeZone,
    profile: fixtureChild.profile,
    collections: [
        {
            id: fixtureCollectionIds.cartoons,
            title: 'Çizgi Film',
            artworkKey: 'bear',
            posterUrl: null,
            itemIds: [contentId('01'), contentId('02'), contentId('03')],
        },
        {
            id: fixtureCollectionIds.songs,
            title: 'Şarkılar',
            artworkKey: 'note',
            posterUrl: null,
            itemIds: [contentId('04'), contentId('05')],
        },
        {
            id: fixtureCollectionIds.morning,
            title: 'Sabah',
            artworkKey: 'sun',
            posterUrl: null,
            itemIds: [contentId('01'), contentId('04')],
        },
    ],
    home: {
        variant: 'grid3',
        sections: [
            {
                kind: 'collection',
                itemIds: [
                    fixtureCollectionIds.cartoons,
                    fixtureCollectionIds.songs,
                    fixtureCollectionIds.morning,
                ],
            },
        ],
    },
    items: [],
    featureFlags: { YOUTUBE: true, IPTV: false, ACTIVE_VIEWING: false },
    entitlements: { MULTI_CHILD: true, MULTI_DEVICE: true, IPTV: false },
};
//# sourceMappingURL=index.js.map